import { useEffect } from "react";
import { animate } from "motion";
import {
    EPS, FULL_RECT, COLLAPSE_BL, COLLAPSE_TR,
    clamp01, polygonHalfPlane, clipPathFromPts, setLine,
    DEFAULT_ANIMATION, DEFAULT_SLOPE,
} from "../utils/wipeAnimationUtils";
import { profEffect } from "@/utils/renderProfiler";

/**
 * Attaches the diagonal wipe animation to a card element.
 *
 * @param {object} params
 * @param {React.RefObject} params.cardRef    - ref on the root Card element (event target)
 * @param {React.RefObject} params.summaryRef - ref on the "always visible" summary overlay
 * @param {React.RefObject} params.detailsRef - ref on the "hover revealed" details overlay
 * @param {React.RefObject} params.lineRef    - ref on the thin wipe-line div
 * @param {boolean}         [params.enabled=true]  - when false the animation is not attached
 *                                                   (e.g. showedBack=false on clip cards)
 * @param {any[]}           [params.deps=[]]        - extra entries for the useEffect dep array;
 *                                                   pass [flipped] for IngestionLiveCard so the
 *                                                   effect re-initialises after a card flip
 */
export function useWipeAnimation({
    cardRef,
    summaryRef,
    detailsRef,
    lineRef,
    enabled = true,
    deps = [],
    onHoverEnter,
    onHoverLeave,
}) {
    useEffect(() => profEffect("effect:WipeCard(useWipeAnimation)", () => {
        const cardEl    = cardRef.current;
        const summaryEl = summaryRef.current;
        const detailsEl = detailsRef.current;
        const lineEl    = lineRef.current;
        if (!cardEl || !summaryEl || !detailsEl || !lineEl) return;

        const slope = DEFAULT_SLOPE;
        const state = { t: 0 };
        let currentAnimation = null;
        let debounceTimer    = null;

        const apply = (t) => {
            const tt = clamp01(t);
            if (tt <= EPS) {
                detailsEl.style.clipPath = COLLAPSE_BL;
                summaryEl.style.clipPath = FULL_RECT;
                setLine(lineEl, 0, slope.x, slope.y);
                return;
            }
            if (tt >= 1 - EPS) {
                detailsEl.style.clipPath = FULL_RECT;
                summaryEl.style.clipPath = COLLAPSE_TR;
                setLine(lineEl, 1, slope.x, slope.y);
                return;
            }
            detailsEl.style.clipPath = clipPathFromPts(polygonHalfPlane(tt, true,  slope.x, slope.y));
            summaryEl.style.clipPath = clipPathFromPts(polygonHalfPlane(tt, false, slope.x, slope.y));
            setLine(lineEl, tt, slope.x, slope.y);
        };

        apply(0);

        const animateTo = (target) => {
            if (currentAnimation) { currentAnimation.cancel(); currentAnimation = null; }
            if (Math.abs(state.t - target) < EPS) return;
            currentAnimation = animate(state, { t: target }, {
                ...DEFAULT_ANIMATION,
                onUpdate:  () => apply(state.t),
                onComplete: () => { state.t = target; apply(target); currentAnimation = null; },
            });
        };

        const debouncedAnimateTo = (target) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => animateTo(target), 500);
        };

        const onEnter = () => { debouncedAnimateTo(1); onHoverEnter?.(); };
        const onLeave = () => { debouncedAnimateTo(0); onHoverLeave?.(); };

        if (enabled) {
            cardEl.addEventListener("pointerenter", onEnter);
            cardEl.addEventListener("pointerleave", onLeave);
        }

        return () => {
            clearTimeout(debounceTimer);
            if (currentAnimation) currentAnimation.cancel();
            if (enabled) {
                cardEl.removeEventListener("pointerenter", onEnter);
                cardEl.removeEventListener("pointerleave", onLeave);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), deps);
}
